import { SubmitHandler } from 'react-hook-form'
import { toast } from 'react-toastify'
import { ReactNode, FC } from 'react'
import { usePathname } from 'next/navigation'

import { Section } from 'components/section'
import { Heading } from 'components/heading'
import {
  EMAIL_RULE,
  EmailFormField,
  FIRST_NAME_RULE,
  FirstNameFormField,
  LAST_NAME_RULE,
  LastNameFormField,
  MESSAGE_RULE,
  SelectArrayOption,
} from 'components/form-field'
import { SubmitFormButton } from 'components/submit-form-button'
import { Form, useForm } from 'components/form'
import { TextareaFormField } from 'components/form-field/TextareaFormField'
import { RadioFormField, RadioFormFieldGroup } from 'components/form-field/RadioFormField'
import { FormNameType } from 'utils/types'
import { yup } from 'components/form/yup'
import Mail from 'icons/mail.svg'
import classes from 'components/contact-us/contact-us.module.css'
import { BrokerModelWithPermissions } from 'utils/get-server-side-props-utils/withAuthUser'
import { routes } from 'utils/routes'

import { nest } from '../../api'
import { ContactSourceEnum, ContactUsCreateModel } from '../../api/nest/contact-us/types'

export const CONTACT_FIELDS_NAME: FormNameType<Omit<ContactUsCreateModel, 'source'>> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  text: 'text',
  whoAmI: 'whoAmI',
}

export const CONTACT_VALIDATOR = yup.object().shape({
  [CONTACT_FIELDS_NAME.firstName]: FIRST_NAME_RULE,
  [CONTACT_FIELDS_NAME.lastName]: LAST_NAME_RULE,
  [CONTACT_FIELDS_NAME.email]: EMAIL_RULE,
  [CONTACT_FIELDS_NAME.text]: MESSAGE_RULE.required(),
  [CONTACT_FIELDS_NAME.whoAmI]: yup.string().required(),
})

export interface ContactUsProps {
  description: ReactNode
  email: ReactNode
  contactSectionTitle: ReactNode
  iAmList: SelectArrayOption[]
  authUser?: BrokerModelWithPermissions
}

export const ContactUs: FC<ContactUsProps> = ({
  email,
  description,
  contactSectionTitle,
  iAmList,
  authUser,
}) => {
  const pathname = usePathname()

  const source = pathname?.startsWith(routes.partnerPortal)
    ? ContactSourceEnum.PARTNER_PORTAL
    : ContactSourceEnum.WEBSITE

  const formMethods = useForm(CONTACT_VALIDATOR, {
    defaultValues: authUser
      ? {
          [CONTACT_FIELDS_NAME.firstName]: authUser.firstName ?? '',
          [CONTACT_FIELDS_NAME.lastName]: authUser.lastName ?? '',
          [CONTACT_FIELDS_NAME.email]: authUser.email ?? '',
        }
      : {},
  })

  const onSubmit: SubmitHandler<ContactUsCreateModel> = async (data) => {
    await nest.contactUs.create({ ...data, source })
    toast.success('Your message has been sent. We will contact you shortly!')
    formMethods.reset()
  }

  return (
    <Section className={classes.section} theme="hero">
      <div className={classes.pageDescription}>
        <Heading className={classes.mainTitle} theme="h1">
          Contact REZITRADE
        </Heading>
        <p className={classes.subTitle}>{description}</p>
        <div className={classes.info}>
          <Heading theme="h5" className={classes.infoTitle}>
            {contactSectionTitle}
          </Heading>
          <div className={classes.sendMail}>
            <Mail className={classes.mailLogo} />
            <Heading theme="h6" className={classes.mailTitle}>
              Send us an email
            </Heading>
            <a href={`mailto:${email}`} className={classes.contactEmail}>
              {email}
            </a>
          </div>
        </div>
      </div>
      <div className={classes.contactFormWrapper}>
        <Form formMethods={formMethods} onSubmit={onSubmit} className={classes.form}>
          <div className={classes.formFields}>
            <RadioFormFieldGroup
              className={classes.formRadioGroup}
              fieldName={CONTACT_FIELDS_NAME.whoAmI}
              groupTitle="I am a:"
              theme="pills"
            >
              {iAmList.map(({ value, label }) => (
                <RadioFormField
                  value={value}
                  key={value}
                  label={label}
                  theme="pill_primary"
                  className={classes.radioButton}
                  classNameLabel={classes.radioLabel}
                />
              ))}
            </RadioFormFieldGroup>
            <FirstNameFormField
              name={CONTACT_FIELDS_NAME.firstName}
              label={null}
              fullWidth
              placeholder="Your first name"
            />
            <LastNameFormField
              name={CONTACT_FIELDS_NAME.lastName}
              label={null}
              fullWidth
              placeholder="Your last name"
            />
            <EmailFormField
              name={CONTACT_FIELDS_NAME.email}
              label={null}
              fullWidth
              placeholder="Your email address"
            />
            <TextareaFormField
              name={CONTACT_FIELDS_NAME.text}
              className={classes.textareaWrapper}
              label="How can we help?"
            />
            <SubmitFormButton theme="primary" className={classes.submit}>
              Submit
            </SubmitFormButton>
          </div>
        </Form>
      </div>
    </Section>
  )
}
